import Select from "components/shared/inputs/select";
import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useMemo, useState } from "react";
import Input, { InputType } from "components/shared/inputs/input";
import {
  useCreateAdminUserMutation,
  useGetEnvironnementsQuery,
  useGetStandardUsersQuery,
  useUpdateAdminUserMutation,
} from "store/api";
import useTranslation from "utils/hooks/useTranslation";

type EditableAdminUserRow = {
  key?: number | string;
  id?: number | string;
  uid?: number | string;
  username?: string;
  email?: string;
  entities?: unknown;
};

type AdminUserPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  mode?: "create" | "edit";
  row?: EditableAdminUserRow | null;
  onSaved?: () => Promise<void> | void;
};

function parseEntityIds(raw: unknown): Array<number | string> {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => (typeof item === "number" ? item : String(item).trim()))
      .filter((item) => String(item).length > 0);
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "number" ? item : String(item).trim()))
          .filter((item) => String(item).length > 0);
      }
    } catch {
      // not a JSON array
    }

    return trimmed
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

const AdminUserPopup: React.FC<AdminUserPopupProps> = ({
  isOpen,
  onClose,
  mode = "edit",
  row,
  onSaved,
}) => {
  const { t } = useTranslation();
  const isEditMode = mode === "edit";
  const [isSaving, setIsSaving] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | string | null>(null);
  const [selectedEntityIds, setSelectedEntityIds] = useState<Array<number | string>>([]);

  const { data: environnements = [] } = useGetEnvironnementsQuery("admin-user-popup", {
    skip: !isOpen,
  });
  const { data: standardUsers = [] } = useGetStandardUsersQuery("admin-user-popup", {
    skip: !isOpen || isEditMode,
  });

  const [createAdminUser] = useCreateAdminUserMutation();
  const [updateAdminUser] = useUpdateAdminUserMutation();

  useEffect(() => {
    if (!isOpen) return;

    setIsSaving(false);
    if (isEditMode) {
      const rowId = row?.id ?? row?.uid ?? row?.key ?? null;
      setSelectedUserId(rowId);
      setSelectedEntityIds(parseEntityIds(row?.entities));
      return;
    }

    setSelectedUserId(null);
    setSelectedEntityIds([]);
  }, [isOpen, isEditMode, row?.entities, row?.id, row?.key, row?.uid]);

  const userOptions = useMemo(
    () =>
      (Array.isArray(standardUsers) ? standardUsers : []).map((u: any) => ({
        value: u.id,
        text: `${u.username ?? ""} ${u.email ?? ""}`.trim(),
      })),
    [standardUsers]
  );

  const environmentOptions = useMemo(
    () =>
      (Array.isArray(environnements) ? environnements : []).map((e: any) => ({
        value: e.id,
        label: `${e.country ?? e.code ?? e.id}`,
        code: e.code,
      })),
    [environnements]
  );

  const handleSave = async () => {
    const uid = isEditMode ? row?.id ?? row?.uid ?? row?.key : selectedUserId;
    if (uid == null) return;

    setIsSaving(true);
    try {
      if (isEditMode) {
        await updateAdminUser({
          id: uid,
          data: { entities: selectedEntityIds },
        }).unwrap();
      } else {
        await createAdminUser({
          uid,
          entities: selectedEntityIds,
        }).unwrap();
      }

      onClose();
      void Promise.resolve(onSaved?.()).catch(() => {
        // refresh failure should not keep popup open after a successful save
      });
    } catch {
      // handled globally by interceptor
    } finally {
      setIsSaving(false);
    }
  };

  const toggleEntity = (envId: number | string) => {
    setSelectedEntityIds((prev) => {
      const key = String(envId);
      const exists = prev.some((id) => String(id) === key);
      if (exists) {
        return prev.filter((id) => String(id) !== key);
      }
      return [...prev, key];
    });
  };

  if (!isOpen) return null;

  console.log(userOptions);
  

  return (
    <Popup
      title={isEditMode ? "Modifier droits admin" : "Ajouter droits admin"}
      onClose={onClose}
      portal
      fixSize="middle"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving || selectedUserId == null}>
              {t(isEditMode ? "Update" : "Add")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
      >
        <group data-direction="column" data-gap="10">
          <text data-weight="700">{t("user")}</text>
          {isEditMode ? (
            <text>{`${row?.username ?? ""} ${row?.email ?? ""}`.trim()}</text>
          ) : (
            <Select
              value={selectedUserId ?? undefined}
              options={userOptions}
              onChange={(value: any) => setSelectedUserId(value ?? null)}
              allowSearch
              virtual
              placeholder={t("user")}
            />

          )}
        </group>

        <group data-direction="column" data-gap="8" data-space-top="10">
          <text data-weight="700">{t("environnement_mgt")}</text>
          <group data-gap="20" data-wrap="wrap">
            {environmentOptions.length === 0 && <text>{t("No data")}</text>}
            {environmentOptions.map((env) => {
              const checked = selectedEntityIds.some((id) => String(id) === String(env.value));
              return (
                <group key={String(env.value)} data-width="auto">
                  <Input
                    id={`admin-user-env-${env.code ?? env.value}`}
                    type={InputType.Checkbox}
                    name={`env_${env.value}`}
                    label={env.label}
                    checked={checked}
                    onCustomChange={() => toggleEntity(env.value)}
                  />
                </group>
              );
            })}
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default AdminUserPopup;
